/**
 * Helpers for Max mode: insert markdown `![alt](url)` from raster files.
 * PDFs use `data:application/pdf;npp-page=N;base64,…` (see {@link mountMaxPdfRasterImages} in maxPdfPageRaster).
 */

/**
 * `<input type="file" accept>` for formats browsers typically decode as images (same idea as image tabs / Open).
 */
export const MAX_BROWSER_IMAGE_FILE_ACCEPT =
  "image/*,.png,.jpg,.jpeg,.jfif,.webp,.gif,.bmp,.svg,.ico,.avif,.heic,.heif";

/** `<input type="file" accept>` for PDF insert (page is stored in `#page=N` on the data URL). */
export const MAX_BROWSER_PDF_FILE_ACCEPT = ".pdf,application/pdf";

/** Read a local image file as a `data:image/…;base64,…` URL for embedding in Max markdown. */
export function readImageFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const out = r.result;
      if (typeof out !== "string") {
        reject(new Error("FileReader did not return a data URL"));
        return;
      }
      resolve(out);
    };
    r.onerror = () => reject(r.error ?? new Error("Failed to read image file"));
    r.readAsDataURL(file);
  });
}

/** Markdown image line for Max prose (GFM); wraps URL in `<>` when needed. */
export function buildMaxMarkdownImageLine(alt: string, url: string): string {
  const a = alt.replace(/[\[\]\r\n]/g, " ").trim() || "image";
  const needsWrap = /[)\s#;]/.test(url) || url.includes("<");
  const dest = needsWrap ? `<${url}>` : url;
  return `![${a}](${dest})\n\n`;
}
